import { sx } from '../../ui/designStyles';
import { bs, type BadgeWord } from './lib';
import { useShownHonesty } from '../../session/DemoScope';
import { isEstimateWord } from '@aithora/shared';
import { Est, NOT_AVAILABLE, useBuildLabels, useDevNotes } from '../../ui/buildLabels';

export interface Kpi {
  k: string;
  v: string;
  /** Null: no figure to vouch for (the not-set state), so no badge. */
  badge: BadgeWord | null;
  note: string;
  pts: string;
  c: string;
  tip: string;
  /** The note opens an editor (restored from production: cash and burn). */
  onNote?: () => void;
  noteLabel?: string;
}

/** The KPI strip under a scope's title (design fa.kpis / ff.kpis, T10241-10250). */
export function KpiStrip({ kpis, testId }: { kpis: Kpi[]; testId?: string }) {
  // In a seeded building scope (no banner, no "demo" on screen): no SAMPLE chip on each figure.
  const tag = useShownHonesty();
  // A customer sees a quiet "est." after a modeled or estimated figure (Andrew 2026-10-15).
  const build = useBuildLabels();
  return (
    <div
      data-testid={testId}
      style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(128px,1fr));gap:10px 16px;min-width:0;padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.12);')}
    >
      {kpis.map((k) => (
        <div key={k.k} title={k.tip} style={sx('min-width:0;display:flex;flex-direction:column;gap:3px;')} data-testid="finance-kpi">
          <span style={sx('display:flex;align-items:center;gap:3px 6px;flex-wrap:wrap;min-width:0;')}>
            <span style={sx('font-size:14px;font-weight:700;color:#3C454F;white-space:nowrap;')}>
              <span>{k.k}</span>
            </span>
            {k.badge && tag(k.badge) && (
              <span style={sx(bs(k.badge))} data-honesty={k.badge}>
                <span>{k.badge}</span>
              </span>
            )}
          </span>
          {/* No figure is neither good nor bad: an empty value reads in neutral grey, never its tone. */}
          <span data-countup="1" data-empty={k.v === '–' ? 'true' : undefined} style={sx('font-size:22px;font-weight:800;letter-spacing:-.03em;font-variant-numeric:tabular-nums;line-height:1.05;color:' + (k.v === '–' ? '#54606C' : k.c) + ';')}>
            <span>{k.v}</span>
            {!build && isEstimateWord(k.badge) && <Est />}
          </span>
          <svg viewBox="0 0 100 24" preserveAspectRatio="none" style={sx('width:100%;height:20px;display:block;')} aria-hidden="true">
            <polyline points={k.pts} fill="none" stroke={k.c} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          </svg>
          {k.onNote ? (
            <a href="#" aria-label={k.noteLabel} title={k.noteLabel} onClick={(e) => { e.preventDefault(); k.onNote?.(); }} style={sx('font-size:14px;color:#54606C;line-height:1.3;text-decoration:none;')}>
              <span>{k.note}</span>
            </a>
          ) : (
            <span style={sx('font-size:14px;color:#54606C;line-height:1.3;')}>
              <span>{k.note}</span>
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** A block's head: title, meta, and anything after (design T10253 and the others like it). */
export function BlockHead({ title, meta, children }: { title: string; meta?: string; children?: React.ReactNode }) {
  return (
    <div style={sx('display:flex;align-items:baseline;gap:2px 10px;flex-wrap:wrap;padding-bottom:6px;border-bottom:1px solid rgba(34,39,46,.12);min-width:0;')}>
      <span style={sx('font-size:14px;font-weight:800;color:#22272E;letter-spacing:-.01em;white-space:nowrap;')}>
        <span>{title}</span>
      </span>
      {meta !== undefined && (
        <span style={sx('font-size:14px;color:#54606C;')}>
          <span>{meta}</span>
        </span>
      )}
      {children}
    </div>
  );
}

/** A line saying a block waits on a route that is not served yet. */
export function Waiting({ what }: { what: string }) {
  // The route it waits on is a developer note: everyone else reads a plain line.
  const dev = useDevNotes();
  return (
    <span style={sx('font-size:14px;color:#54606C;line-height:1.4;padding:4px 0;')} data-testid="empty-state">
      {dev ? `Not served yet: ${what}` : NOT_AVAILABLE}
    </span>
  );
}
